import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function LeadCaptureSection() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { ref, visible } = useScrollReveal({ threshold: 0.1 });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");

    try {
      const formData = new URLSearchParams();
      formData.append("name", name);
      formData.append("email", email);

      const response = await fetch(
        "https://readdy.ai/api/form/d8q759gn4gr6p3b2g58g",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        if (typeof window !== "undefined" && (window as Record<string, unknown>).dataLayer) {
          ((window as Record<string, unknown>).dataLayer as Array<Record<string, unknown>>).push({ event: "lead_captured" });
        }
        setStatus("success");
        setTimeout(() => {
          navigate("/the-joint-mobility-report");
        }, 1200);
      } else {
        setStatus("error");
        setMessage("Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setMessage("Connection error. Check your internet and try again.");
    }
  };

  return (
    <section ref={ref} className="relative py-14 md:py-18 bg-background-50">
      <div className={`max-w-5xl mx-auto px-4 md:px-8 transition-all duration-600 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{ transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)" }}>
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="flex flex-col md:flex-row">
            {/* ===== LEFT — Book Cover ===== */}
            <div className="w-full md:w-[35%] bg-gradient-to-br from-primary-100 via-accent-50 to-background-50 flex items-center justify-center p-6 md:p-8">
              <div className="relative w-full max-w-[240px] aspect-[3/4]">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20premium%20ebook%20cover%20mockup%20with%20bold%20title%20THE%20JOINT%20MOBILITY%20REPORT%20clearly%20visible%20on%20the%20front%20cover%2C%20warm%20terracotta%20and%20cream%20color%20palette%2C%20sophisticated%20editorial%20design%2C%20magazine%20quality%20book%20cover%2C%20golden%20accent%20details%2C%20soft%20dramatic%20lighting%2C%20premium%20health%20publication%20look%2C%203D%20render%20style%20with%20gentle%20shadow%20on%20light%20surface&width=480&height=640&seq=ebook-cover-v2&orientation=portrait"
                  alt="The Joint Mobility Report - Free Premium Health Guide"
                  className="w-full h-full object-cover rounded-lg hover-img-zoom"
                  width={480}
                  height={640}
                />
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 bg-primary-500 text-white text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                    <i className="ri-sparkling-line text-xs" />
                    FREE
                  </span>
                </div>
              </div>
            </div>

            {/* ===== RIGHT — Form ===== */}
            <div className="w-full md:w-[65%] p-8 md:p-12 flex flex-col justify-center">
              <p className="text-accent-600 text-sm md:text-base font-semibold tracking-[0.15em] uppercase mb-2">
                <i className="ri-book-open-line mr-1.5" />
                Exclusive Editorial Report
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-2">
                Download Free Guide
              </h2>
              <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
                The Joint Mobility Report — 7 Hidden Habits That Accelerate Joint Aging After 50
              </p>

              {status === "success" ? (
                <div className="bg-accent-50 border border-accent-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-950 font-semibold text-lg">Guide sent successfully!</p>
                      <p className="text-foreground-600 text-base">Redirecting to your report...</p>
                    </div>
                  </div>
                  <div className="w-full bg-accent-200 rounded-full h-1 mt-3 overflow-hidden">
                    <div className="bg-primary-500 h-full rounded-full animate-pulse" style={{ width: "100%" }} />
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="lead-name" className="block text-foreground-800 text-base font-medium mb-1.5">
                      Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                        <i className="ri-user-line text-foreground-500 text-lg" />
                      </div>
                      <input
                        id="lead-name"
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your full name"
                        required
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-background-100 border border-background-300 text-foreground-950 placeholder-foreground-400 text-base focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="lead-email" className="block text-foreground-800 text-base font-medium mb-1.5">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                        <i className="ri-mail-line text-foreground-500 text-lg" />
                      </div>
                      <input
                        id="lead-email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        required
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-background-100 border border-background-300 text-foreground-950 placeholder-foreground-400 text-base focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-primary-500 text-foreground-950 font-bold text-lg hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all btn-hover-lift whitespace-nowrap cursor-pointer uppercase tracking-wide"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-xl" />
                        SENDING...
                      </>
                    ) : (
                      <>
                        DOWNLOAD FREE GUIDE
                        <i className="ri-arrow-right-line text-xl" />
                      </>
                    )}
                  </button>

                  <p className="text-foreground-500 text-sm text-center flex items-center justify-center gap-1.5 pt-1">
                    <i className="ri-shield-check-line text-primary-500" />
                    Your data is 100% secure. No spam.
                  </p>
                </form>
              )}

              {status === "error" && (
                <p className="text-red-600 text-base mt-3 text-center">{message}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}